import type { BoardDocument, Frame, ModuleInstanceDocument, ModuleVersionRecord, StudioBootstrap } from "@contracts";
import { ulid } from "ulid";
import { create } from "zustand";
import { temporal } from "zundo";

export type PreviewMode = "design" | "gray" | "device";
export type LibraryTab = "modules" | "assets";

interface StudioState {
  bootstrap: StudioBootstrap | null;
  board: BoardDocument | null;
  activeVariantId: string;
  selectedIds: string[];
  selectedAssetId: string | null;
  previewMode: PreviewMode;
  fixtureState: string;
  libraryTab: LibraryTab;
  dirty: boolean;
  saveState: "idle" | "saving" | "saved" | "error";
  lastPreviewAt: string | null;
  load: (bootstrap: StudioBootstrap) => void;
  replaceBootstrap: (bootstrap: StudioBootstrap) => void;
  setBoard: (board: BoardDocument, dirty?: boolean) => void;
  setActiveVariant: (id: string) => void;
  select: (ids: string[], assetId?: string | null) => void;
  setPreviewMode: (mode: PreviewMode) => void;
  setFixtureState: (state: string) => void;
  setLibraryTab: (tab: LibraryTab) => void;
  setSaveState: (state: StudioState["saveState"]) => void;
  setLastPreviewAt: (value: string) => void;
  updateFrame: (id: string, patch: Partial<Frame>) => void;
  updateFrames: (patches: Array<{ id: string; patch: Partial<Frame> }>) => void;
  updateProps: (id: string, props: Record<string, unknown>) => void;
  updateFallback: (id: string, mode: ModuleInstanceDocument["fallback"]["mode"]) => void;
  updateBinding: (id: string, input: string, sourceInstanceId: string, path: string) => void;
  addModule: (module: ModuleVersionRecord, x?: number, y?: number, assetId?: string) => string | null;
  duplicateSelected: () => void;
  removeSelected: () => void;
  toggleLock: () => void;
  moveLayer: (direction: "front" | "back" | "up" | "down") => void;
}

function updateInstances(board: BoardDocument | null, activeVariantId: string, updater: (instances: ModuleInstanceDocument[]) => ModuleInstanceDocument[]): BoardDocument | null {
  if (!board) return board;
  return {
    ...board,
    updatedAt: new Date().toISOString(),
    variants: board.variants.map((variant) => variant.id === activeVariantId ? { ...variant, instances: updater(variant.instances) } : variant),
  };
}

function defaultBindings(module: ModuleVersionRecord, bootstrap: StudioBootstrap): ModuleInstanceDocument["bindings"] {
  const fields = Object.keys(module.manifest.dataSchema.properties ?? {});
  const category = module.category;
  const preferredDefinition = category === "weather" ? "builtin.open-meteo" : category === "system" ? "builtin.system-metrics" : category === "quota" ? "builtin.codex-usage" : null;
  const source = preferredDefinition ? bootstrap.sources.find((item) => item.definitionId === preferredDefinition) : null;
  if (!source) return [];
  const definition = bootstrap.sourceDefinitions.find((item) => item.id === source.definitionId);
  const outputFields = new Set(Object.keys(definition?.outputSchema.properties ?? {}));
  return fields.filter((field) => outputFields.has(field)).map((field) => ({ input: field, sourceInstanceId: source.id, path: field }));
}

export const useStudio = create<StudioState>()(temporal((set, get) => ({
  bootstrap: null,
  board: null,
  activeVariantId: "",
  selectedIds: [],
  selectedAssetId: null,
  previewMode: "design",
  fixtureState: "live",
  libraryTab: "modules",
  dirty: false,
  saveState: "idle",
  lastPreviewAt: null,
  load: (bootstrap) => set({ bootstrap, board: bootstrap.board, activeVariantId: bootstrap.activeVariantId, selectedIds: [bootstrap.board.variants[0]?.instances[1]?.id].filter(Boolean) as string[], selectedAssetId: null, dirty: false, saveState: "saved" }),
  replaceBootstrap: (bootstrap) => set({ bootstrap, board: bootstrap.board, activeVariantId: bootstrap.activeVariantId, dirty: false, saveState: "saved" }),
  setBoard: (board, dirty = true) => set({ board, dirty, saveState: dirty ? "idle" : "saved" }),
  setActiveVariant: (activeVariantId) => set({ activeVariantId, selectedIds: [], selectedAssetId: null }),
  select: (selectedIds, selectedAssetId = null) => set({ selectedIds, selectedAssetId }),
  setPreviewMode: (previewMode) => set({ previewMode }),
  setFixtureState: (fixtureState) => set({ fixtureState }),
  setLibraryTab: (libraryTab) => set({ libraryTab }),
  setSaveState: (saveState) => set({ saveState, dirty: saveState === "saved" ? false : get().dirty }),
  setLastPreviewAt: (lastPreviewAt) => set({ lastPreviewAt }),
  updateFrame: (id, patch) => set((state) => ({ board: updateInstances(state.board, state.activeVariantId, (items) => items.map((item) => item.id === id ? { ...item, frame: { ...item.frame, ...patch } } : item)), dirty: true, saveState: "idle" })),
  updateFrames: (patches) => set((state) => {
    const byId = new Map(patches.map((item) => [item.id, item.patch]));
    return { board: updateInstances(state.board, state.activeVariantId, (items) => items.map((item) => byId.has(item.id) ? { ...item, frame: { ...item.frame, ...byId.get(item.id) } } : item)), dirty: true, saveState: "idle" };
  }),
  updateProps: (id, props) => set((state) => ({ board: updateInstances(state.board, state.activeVariantId, (items) => items.map((item) => item.id === id ? { ...item, props } : item)), dirty: true, saveState: "idle" })),
  updateFallback: (id, mode) => set((state) => ({ board: updateInstances(state.board, state.activeVariantId, (items) => items.map((item) => item.id === id ? { ...item, fallback: { mode } } : item)), dirty: true, saveState: "idle" })),
  updateBinding: (id, input, sourceInstanceId, path) => set((state) => ({
    board: updateInstances(state.board, state.activeVariantId, (items) => items.map((item) => item.id === id ? {
      ...item,
      bindings: [...item.bindings.filter((binding) => binding.input !== input), ...(sourceInstanceId && path ? [{ input, sourceInstanceId, path }] : [])],
    } : item)),
    dirty: true,
    saveState: "idle",
  })),
  addModule: (module, x, y, assetId) => {
    const state = get();
    const variant = state.board?.variants.find((item) => item.id === state.activeVariantId);
    if (!state.bootstrap || !variant) return null;
    const width = Math.min(Math.max(module.manifest.constraints.minWidth, 240), variant.canvas.width - 40);
    const aspect = module.manifest.constraints.preferredAspectRatio ?? 1.5;
    const height = Math.min(Math.max(module.manifest.constraints.minHeight, width / aspect), variant.canvas.height - 40);
    const id = `instance_${ulid()}`;
    const highest = Math.max(0, ...variant.instances.map((item) => item.frame.z));
    const instance: ModuleInstanceDocument = {
      id,
      moduleVersionId: module.id,
      frame: {
        x: Math.max(0, Math.min(variant.canvas.width - width, x ?? (variant.canvas.width - width) / 2)),
        y: Math.max(0, Math.min(variant.canvas.height - height, y ?? (variant.canvas.height - height) / 2)),
        width,
        height,
        z: highest + 1,
        rotation: 0,
        locked: false,
        visible: true,
      },
      props: Object.fromEntries(Object.entries(module.manifest.propsSchema.properties ?? {}).map(([key, schema]) => [key, schema.default]).filter(([, value]) => value !== undefined)),
      bindings: defaultBindings(module, state.bootstrap),
      fallback: { mode: "last-valid" },
    };
    if (assetId) instance.props.assetId = assetId;
    set({ board: updateInstances(state.board, state.activeVariantId, (items) => [...items, instance]), selectedIds: [id], selectedAssetId: null, dirty: true, saveState: "idle" });
    return id;
  },
  duplicateSelected: () => set((state) => {
    const selected = new Set(state.selectedIds);
    const ids: string[] = [];
    const board = updateInstances(state.board, state.activeVariantId, (items) => {
      const highest = Math.max(0, ...items.map((item) => item.frame.z));
      const copies = items.filter((item) => selected.has(item.id)).map((item, index) => {
        const id = `instance_${ulid()}`;
        ids.push(id);
        return { ...structuredClone(item), id, frame: { ...item.frame, x: item.frame.x + 16, y: item.frame.y + 16, z: highest + index + 1 } };
      });
      return [...items, ...copies];
    });
    return { board, selectedIds: ids, dirty: true, saveState: "idle" };
  }),
  removeSelected: () => set((state) => ({ board: updateInstances(state.board, state.activeVariantId, (items) => items.filter((item) => !state.selectedIds.includes(item.id))), selectedIds: [], dirty: true, saveState: "idle" })),
  toggleLock: () => set((state) => ({ board: updateInstances(state.board, state.activeVariantId, (items) => items.map((item) => state.selectedIds.includes(item.id) ? { ...item, frame: { ...item.frame, locked: !item.frame.locked } } : item)), dirty: true, saveState: "idle" })),
  moveLayer: (direction) => set((state) => ({ board: updateInstances(state.board, state.activeVariantId, (items) => {
    if (state.selectedIds.length !== 1) return items;
    const target = items.find((item) => item.id === state.selectedIds[0]);
    if (!target) return items;
    const values = items.map((item) => item.frame.z);
    const z = direction === "front" ? Math.max(...values) + 1 : direction === "back" ? Math.min(...values) - 1 : target.frame.z + (direction === "up" ? 1 : -1);
    return items.map((item) => item.id === target.id ? { ...item, frame: { ...item.frame, z } } : item);
  }), dirty: true, saveState: "idle" })),
}), {
  limit: 80,
  partialize: (state) => ({ board: state.board, activeVariantId: state.activeVariantId, selectedIds: state.selectedIds }),
}));
